import {
  CognitoIdentityClient,
  GetIdCommand,
  GetCredentialsForIdentityCommand,
} from '@aws-sdk/client-cognito-identity';
import type { AWSCredentials } from './types';

/**
 * Exchange a Cognito ID token for temporary IAM credentials from the Identity Pool.
 *
 * Uses GetIdCommand to obtain an identity ID, then GetCredentialsForIdentityCommand
 * to retrieve temporary AWS credentials scoped to the authenticated role.
 */
export async function getIdentityCredentials(
  idToken: string,
  identityPoolId: string,
  userPoolId: string,
  region: string,
): Promise<AWSCredentials> {
  const client = new CognitoIdentityClient({ region });
  const loginKey = `cognito-idp.${region}.amazonaws.com/${userPoolId}`;
  const logins = { [loginKey]: idToken };

  const { IdentityId } = await client.send(
    new GetIdCommand({
      IdentityPoolId: identityPoolId,
      Logins: logins,
    }),
  );

  if (!IdentityId) {
    throw new Error('Failed to retrieve identity ID from Cognito Identity Pool.');
  }

  const { Credentials } = await client.send(
    new GetCredentialsForIdentityCommand({
      IdentityId,
      Logins: logins,
    }),
  );

  if (!Credentials?.AccessKeyId || !Credentials.SecretKey || !Credentials.SessionToken) {
    throw new Error('Failed to retrieve credentials from Cognito Identity Pool.');
  }

  return {
    accessKeyId: Credentials.AccessKeyId,
    secretAccessKey: Credentials.SecretKey,
    sessionToken: Credentials.SessionToken,
    expiration: Credentials.Expiration ?? new Date(Date.now() + 3600 * 1000),
  };
}
